import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Grid, Snackbar, Typography } from "@mui/material";
import { MachineCenterTrafficLights } from "components/MachineCenterTrafficLights/MachineCenterTrafficLights.view";
import { useStyles } from "./ClusterHMICard.styles";
import { useTranslation } from "react-i18next";
import { ClusterUptimeDowntimeBar } from "../ClusterUptimeDowntimeBar/ClusterUptimeDowntimeBar.view";
import { IClusterHMIProps } from "./ClusterHMICardInterfaces";
import { formatCounter, truncateLongText } from "utilities/utilities";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import { DOWNTIMEMETHODCAPTURE, IconsTrafficLightStatusCodes } from "constants/constants";
import { OnlineStatusContext } from "contexts/OnlineStatusContext/OnlineStatusContext";
import { getMachineCentersClusterStatusLocalStorage, setMachineCenterIdHMI } from "utilities/storageUtilities";
import useUptimeDowntimeActionsHMI from "hooks/useUptimeDowntimeActionsHMI";
import { ClusterContext } from "contexts/ClusterContext/ClusterContext";
import { MQTTListenerContext } from "contexts/MQTTListenerContext/MQTTListenerContext";

export const ClusterHMICard = ({ index, machineCenter, clusterHMINumber }: IClusterHMIProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        dispatch: { handleMoveToCluster },
    } = useContext(HMICachedDataContext);
    const { store: clusterStore } = useContext(ClusterCachedDataContext);
    const { updateOnlineStatus } = useContext(OnlineStatusContext);
    const { machineCentersClusterStatus, handleSetMachineCentersClusterStatus } = useContext(ClusterContext);
    const { isListening } = useContext(MQTTListenerContext);
    const { handleHMILogin } = useUptimeDowntimeActionsHMI();

    const [counter, setCounter] = useState("");
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const machineCenterStatusDetails = useMemo(
        () =>
            machineCentersClusterStatus.find(
                (MachineCenterCluster) => MachineCenterCluster.machineCenterId === machineCenter.id,
            ),
        [machineCentersClusterStatus, machineCenter],
    );

    const plcState: boolean = useMemo(
        () =>
            isListening &&
            machineCenter.downtimeMethodCapture === DOWNTIMEMETHODCAPTURE.PLC &&
            Boolean(machineCenter.plcAddress) &&
            !Boolean(machineCenterStatusDetails?.machinePlcDisconnected),
        [isListening, machineCenter, machineCenterStatusDetails?.machinePlcDisconnected],
    );

    const updateMachineCenterStatusToDowntime = useCallback(() => {
        const currentClusterMachinesStatusLocalstorage = machineCentersClusterStatus.map(
            (currentMachineCenterClusterStatus) => {
                if (currentMachineCenterClusterStatus.machineCenterId === machineCenter.id) {
                    return {
                        ...currentMachineCenterClusterStatus,
                        machinePlcDisconnected:
                            machineCenter.downtimeMethodCapture === DOWNTIMEMETHODCAPTURE.PLC && !plcState,
                        status: IconsTrafficLightStatusCodes.DOWN,
                        downtimePending: Boolean(!currentMachineCenterClusterStatus?.failureDetails),
                    };
                } else {
                    return { ...currentMachineCenterClusterStatus };
                }
            },
        );
        handleSetMachineCentersClusterStatus(currentClusterMachinesStatusLocalstorage);
    }, [machineCentersClusterStatus, machineCenter, handleSetMachineCentersClusterStatus, plcState]);

    useEffect(() => {
        if (
            machineCenterStatusDetails?.status === IconsTrafficLightStatusCodes.BELOW_TRIGGER ||
            machineCenterStatusDetails?.status === IconsTrafficLightStatusCodes.DOWN
        ) {
            const updateTime = () => {
                const currentTime = Date.now();
                const duration = currentTime - (machineCenterStatusDetails?.counter || 0);
                const downtimeTrigger = (machineCenter.downtimeTriggerThreshold || 0) * 1000;
                const belowTrigger = downtimeTrigger > duration;
                if (
                    !belowTrigger &&
                    machineCenterStatusDetails?.status === IconsTrafficLightStatusCodes.BELOW_TRIGGER
                ) {
                    updateMachineCenterStatusToDowntime();
                }
                setCounter(formatCounter(duration));
            };

            updateTime();

            const interval = setInterval(updateTime, 1000);

            return () => clearInterval(interval);
        }
    }, [machineCenterStatusDetails, machineCenter.downtimeTriggerThreshold, updateMachineCenterStatusToDowntime]);

    const handleMovingToHMI = useCallback(async () => {
        try {
            setShowErrorMessage(false);
            const currentStatusDetails =
                machineCenterStatusDetails ||
                getMachineCentersClusterStatusLocalStorage()?.find(
                    (MachineCenterCluster) => MachineCenterCluster.machineCenterId === machineCenter.id,
                );
            const currentMachineCenter = {
                machine_center_id: machineCenter.id,
                downtime_method_capture_id: machineCenter.downtimeMethodCapture,
            };
            const machinePlcIsDisconnected =
                machineCenter.downtimeMethodCapture === DOWNTIMEMETHODCAPTURE.PLC && !plcState;
            setMachineCenterIdHMI(currentMachineCenter);
            // The HMI is thought to work as a closed network offline first, so this is only left as a precaution in the possible case of
            // not having the local stored data at hand, we check for connectivity and proceed to update.
            const isOnline = currentStatusDetails ? false : await updateOnlineStatus();
            handleMoveToCluster(clusterStore, machineCenter.id);
            await handleHMILogin(currentMachineCenter, isOnline, currentStatusDetails, machinePlcIsDisconnected);
        } catch {
            setShowErrorMessage(true);
        }
    }, [
        clusterStore,
        handleHMILogin,
        handleMoveToCluster,
        machineCenter.downtimeMethodCapture,
        machineCenter.id,
        machineCenterStatusDetails,
        updateOnlineStatus,
        plcState,
    ]);

    return (
        <Grid
            onClick={handleMovingToHMI}
            item
            style={{
                gridRow: clusterHMINumber <= 2 ? 1 : index < Math.ceil(clusterHMINumber / 2) ? 1 : 2,
                height: "100%",
            }}
            className={`${classes.gridItemContainer} ${
                machineCenterStatusDetails?.downtimePending ? classes.downtimePendingContainer : "none"
            }`}
            sx={{ flexDirection: "column !important" }}
        >
            <div className={classes.plcErrorContainer}>
                <Typography
                    variant="h5"
                    className={`${classes.plcDownWarning} ${
                        clusterHMINumber >= 10
                            ? classes.warningTitleSmall
                            : clusterHMINumber >= 7
                            ? classes.warningTitleMedium
                            : classes.warningTitleLarge
                    }`}
                >
                    {machineCenter.downtimeMethodCapture === DOWNTIMEMETHODCAPTURE.PLC
                        ? !plcState
                            ? t("HMI.cluster.plc_down_error")
                            : ""
                        : ""}
                </Typography>
                <Typography
                    variant="subtitle1"
                    className={`${classes.plcDownWarning} ${
                        clusterHMINumber >= 10
                            ? classes.warningTitleSmall
                            : clusterHMINumber >= 7
                            ? classes.warningTitleMedium
                            : classes.warningTitleLarge
                    }`}
                >
                    {!plcState ? t("HMI.manual_mode_engaged") : ""}
                </Typography>
            </div>
            <div className={classes.trafficLightContainer}>
                <MachineCenterTrafficLights
                    status={machineCenterStatusDetails?.status || IconsTrafficLightStatusCodes.UP}
                    isBottleneck={machineCenter.isBottleneck}
                    noPercentageData={machineCenter.percentages === undefined}
                    bigSize={clusterHMINumber < 7}
                    styleCluster={clusterHMINumber < 7 ? undefined : { width: 60, height: 60 }}
                />
            </div>
            <div className={classes.machineCenterNameContainer}>
                <Typography
                    variant="h3"
                    className={clusterHMINumber <= 7 ? classes.machineCenterNameMedium : classes.machineCenterNameSmall}
                >
                    {truncateLongText(machineCenter.name, 17)}
                </Typography>
                <div className={classes.timerContainer}>
                    <Typography variant="h2">
                        {machineCenterStatusDetails?.status === IconsTrafficLightStatusCodes.DOWN ||
                        machineCenterStatusDetails?.status === IconsTrafficLightStatusCodes.BELOW_TRIGGER
                            ? counter
                            : ""}
                    </Typography>
                </div>
            </div>
            <div className={classes.downtimePendingTitleContainer}>
                {machineCenterStatusDetails?.downtimePending ? (
                    <Typography
                        className={`${classes.downtimePendingTitle} ${
                            clusterHMINumber >= 10
                                ? classes.warningTitleSmall
                                : clusterHMINumber >= 7
                                ? classes.warningTitleMedium
                                : classes.warningTitleLarge
                        }`}
                        variant="h3"
                    >
                        {t("HMI.cluster.downtime_details_pending")}
                    </Typography>
                ) : (
                    ""
                )}
            </div>
            <div className={classes.uptimeDowntimeBarContainer}>
                {machineCenter.percentages ? (
                    <ClusterUptimeDowntimeBar
                        uptime={machineCenter.percentages?.uptime}
                        downtime={machineCenter.percentages.downtime}
                        shift={machineCenter.percentages.shift}
                    />
                ) : (
                    <Typography variant="h5">{t("HMI.cluster.not_enough_data")}</Typography>
                )}
            </div>
            <Snackbar
                open={showErrorMessage}
                autoHideDuration={6000}
                onClose={() => setShowErrorMessage(false)}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity="error" sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{t("mqtt_broker_provider.error")}</span>
                </Alert>
            </Snackbar>
        </Grid>
    );
};
